import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, TableChip, BtnRow, BtnPrimary, BtnLight, BtnSecondary,
  InputLabel, FormSelect, EmptyState, TableWrap, Th, Td,
} from "../components/ui";
import ActionIconButton from "../../../Components/ActionIconButton";
import ActionsCell from "../../../Components/ActionsCell";

function normaliseTerm(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  if (s.match(/term\s*1/i)) return "Term 1";
  if (s.match(/term\s*2/i)) return "Term 2";
  if (s.match(/term\s*3/i)) return "Term 3";
  if (s.match(/term\s*4/i)) return "Term 4";
  return s;
}

function parseAcademicYearStart(academicYear) {
  const m = String(academicYear || "").match(/^(\d{4})/);
  return m ? Number(m[1]) : new Date().getFullYear();
}

function termBaseMonth(term) {
  const t = String(term || "").toLowerCase();
  if (t.includes("term 1")) return 7;
  if (t.includes("term 2")) return 10;
  if (t.includes("term 3")) return 1;
  if (t.includes("term 4")) return 4;
  return 7;
}

function tenureDay(tenure) {
  const t = String(tenure || "").toLowerCase();
  if (t.includes("weekly")) return 5;
  if (t.includes("monthly")) return 15;
  if (t.includes("unit")) return 20;
  if (t.includes("term")) return 24;
  if (t.includes("pre-final")) return 26;
  if (t.includes("final")) return 28;
  return 12;
}

function deriveEventDate(row, index, academicYear) {
  const startYear = parseAcademicYearStart(academicYear);
  const month = termBaseMonth(row.term);
  const year = month >= 7 ? startYear : startYear + 1;
  const day = Math.min(28, tenureDay(row.assessmentTenure) + (index % 3));
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function deriveEventTitle(row) {
  const subject = row.subject || "Subject";
  const chapter = row.chapter || "Chapter";
  const tenure = row.assessmentTenure && row.assessmentTenure !== "—" ? ` (${row.assessmentTenure})` : "";
  return `${subject} - ${chapter}${tenure}`;
}

function buildMonthMatrix(year, month) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const startWeekday = first.getDay();
  const daysInMonth = last.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CUSTOM_EVENT_TYPES = ["Holiday", "Exam", "Meeting", "Special Activity", "Other"];

const CUSTOM_EVENT_COLORS = {
  Holiday:          { bg: "bg-rose-100",   text: "text-rose-700",   border: "border-rose-200",   dot: "#f43f5e" },
  Exam:             { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200", dot: "#8b5cf6" },
  Meeting:          { bg: "bg-teal-100",   text: "text-teal-700",   border: "border-teal-200",   dot: "#14b8a6" },
  "Special Activity":{ bg: "bg-amber-100", text: "text-amber-700",  border: "border-amber-200",  dot: "#f59e0b" },
  Other:            { bg: "bg-gray-100",   text: "text-gray-600",   border: "border-gray-200",   dot: "#6b7280" },
};

function createEmptyCustomEvent(date = "", academicYear = "") {
  return { id: "", title: "", date, eventType: "Other", notes: "", academicYear };
}

export default function Module10AcademicCalendar() {
  const {
    module10CalendarRows,
    setModule10CalendarRows,
    module10CustomEvents,
    setModule10CustomEvents,
    getM6FlatRows,
    module7Assessments,
    getActiveAcademicYear,
    canEditModules1to5,
    module10Loading,
    module10LoadError,
    loadModule10AcademicCalendar,
    saveModule10AcademicCalendar,
    resetModule10AcademicCalendar,
  } = useACPP();

  // ── Custom event modal state ──────────────────────────────────────────────
  const [customModal, setCustomModal] = useState({ open: false, draft: createEmptyCustomEvent(), saving: false });

  const openAddModal = (date = "") => {
    setCustomModal({ open: true, draft: createEmptyCustomEvent(date, getActiveAcademicYear()), saving: false });
  };

  const closeModal = () => setCustomModal({ open: false, draft: createEmptyCustomEvent(), saving: false });

  const setModalField = (field, value) => {
    setCustomModal((prev) => ({ ...prev, draft: { ...prev.draft, [field]: value } }));
  };

  // Auto-persist custom events immediately so a page refresh never loses data
  const persistCustomEvents = async (updatedEvents) => {
    setModule10CustomEvents(updatedEvents);
    try {
      await saveModule10AcademicCalendar(module10CalendarRows, updatedEvents);
    } catch (err) {
      setActionError("Custom event saved locally but failed to sync to server. Please click 'Save Year Data' manually.");
    }
  };

  const handleSaveCustomEvent = async () => {
    const { draft } = customModal;
    if (!draft.title.trim()) { alert("Event title is required."); return; }
    if (!draft.date) { alert("Event date is required."); return; }
    setCustomModal((prev) => ({ ...prev, saving: true }));
    const id = draft.id || `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const updated = draft.id
      ? module10CustomEvents.map((ev) => ev.id === draft.id ? { ...draft, id } : ev)
      : [...module10CustomEvents, { ...draft, id }];
    await persistCustomEvents(updated);
    closeModal();
  };

  const handleEditCustomEvent = (ev) => {
    setCustomModal({ open: true, draft: { ...ev }, saving: false });
  };

  const handleDeleteCustomEvent = async (id) => {
    if (!window.confirm("Remove this custom event?")) return;
    const updated = module10CustomEvents.filter((ev) => ev.id !== id);
    await persistCustomEvents(updated);
  };

  const [filterGrade, setFilterGrade] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterTerm, setFilterTerm] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [moduleStatus, setModuleStatus] = useState("Pending");

  const activeYear = getActiveAcademicYear();
  const startYear = parseAcademicYearStart(activeYear);
  const [viewYear, setViewYear] = useState(startYear);
  const [viewMonth, setViewMonth] = useState(7);

  useEffect(() => {
    setViewYear(startYear);
    setViewMonth(7);
  }, [startYear]);

  useEffect(() => {
    if (!module10Loading) setSavedSnapshot(module10CalendarRows || []);
  }, [module10Loading, module10CalendarRows]);

  useEffect(() => {
    const current = module10CalendarRows || [];
    if (!current.length && !savedSnapshot.length) {
      setModuleStatus("Pending");
      return;
    }
    if (JSON.stringify(current) === JSON.stringify(savedSnapshot)) {
      setModuleStatus("Saved");
      return;
    }
    setModuleStatus("In Progress");
  }, [module10CalendarRows, savedSnapshot]);

  const allM6Rows = useMemo(() => (typeof getM6FlatRows === "function" ? getM6FlatRows() : []), [getM6FlatRows]);
  const filterGrades = [...new Set(allM6Rows.map((r) => r.grade).filter(Boolean))];
  const filterSections = [...new Set(allM6Rows.map((r) => r.section).filter(Boolean))];
  const filterSubjects = [...new Set(allM6Rows.map((r) => r.subject).filter(Boolean))];

  const generate = () => {
    if (!activeYear) { alert("Active ACPP year is missing."); return; }
    if (!allM6Rows.length) { alert("No academic planning rows found from Module 6 yet."); return; }

    const combined = [];
    allM6Rows.forEach((plan) => {
      if (filterGrade && plan.grade !== filterGrade) return;
      if (filterSection && plan.section !== filterSection) return;
      if (filterSubject && plan.subject !== filterSubject) return;

      const chapterLabel = plan.chapterNo && plan.chapterNo !== "—" ? `${plan.chapterNo}. ${plan.chapterName}` : plan.chapterName;
      const termLabel = normaliseTerm(`Term ${plan.term}`);
      if (filterTerm && termLabel !== filterTerm) return;

      const matched = module7Assessments.filter((a) =>
        a.grade === plan.grade &&
        a.section === plan.section &&
        a.subject === plan.subject &&
        Array.isArray(a.chaptersCovered) &&
        a.chaptersCovered.some((c) =>
          c === chapterLabel ||
          c === plan.chapterName ||
          c.includes(plan.chapterName) ||
          plan.chapterName.includes(c.replace(/^\d+\.\s*/, ""))
        )
      );

      if (matched.length) {
        matched.forEach((assessment) => {
          combined.push({
            academicYear: activeYear,
            grade: plan.grade,
            section: plan.section,
            subject: plan.subject,
            term: termLabel,
            unitNo: plan.unitNo,
            unitName: plan.unitName,
            chapter: plan.chapterName,
            assessmentTenure: assessment.tenure || "—",
            assessmentType: assessment.assessmentType || "—",
            assessmentSubTypes: Array.isArray(assessment.assessmentSubTypes) ? assessment.assessmentSubTypes.join(", ") : "—",
          });
        });
      } else {
        combined.push({
          academicYear: activeYear,
          grade: plan.grade,
          section: plan.section,
          subject: plan.subject,
          term: termLabel,
          unitNo: plan.unitNo,
          unitName: plan.unitName,
          chapter: plan.chapterName,
          assessmentTenure: "—",
          assessmentType: "—",
          assessmentSubTypes: "—",
        });
      }
    });

    const withEvents = combined.map((row, index) => ({
      ...row,
      eventDate: deriveEventDate(row, index, activeYear),
      eventTitle: deriveEventTitle(row),
    }));
    setModule10CalendarRows(withEvents);
    setActionError("");
  };

  const save = async () => {
    if (!module10CalendarRows.length && !module10CustomEvents.length) {
      alert("Generate at least one calendar row or add a custom event before saving.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      const saved = await saveModule10AcademicCalendar(module10CalendarRows, module10CustomEvents);
      setSavedSnapshot(saved || []);
      alert("Academic Calendar saved for current ACPP year.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 10 data.");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setSaving(true);
    setActionError("");
    try {
      await loadModule10AcademicCalendar();
    } catch (error) {
      setActionError(error?.message || "Failed to refresh Module 10 data.");
    } finally {
      setSaving(false);
    }
  };

  const resetYearData = async () => {
    setSaving(true);
    setActionError("");
    try {
      await resetModule10AcademicCalendar();
      setSavedSnapshot([]);
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 10 data.");
    } finally {
      setSaving(false);
    }
  };

  const summaryGroups = useMemo(() => {
    const grouped = {};
    module10CalendarRows.forEach((item) => {
      const key = `${item.grade}__${item.section}__${item.subject}`;
      if (!grouped[key]) grouped[key] = { grade: item.grade, section: item.section, subject: item.subject, terms: new Set(), units: new Set(), chapters: new Set(), assessmentTypes: new Set() };
      if (item.term && item.term !== "—") grouped[key].terms.add(item.term);
      if (item.unitNo && item.unitNo !== "—") grouped[key].units.add(`${item.unitNo}__${item.unitName}`);
      if (item.chapter && item.chapter !== "—") grouped[key].chapters.add(item.chapter);
      if (item.assessmentTenure && item.assessmentTenure !== "—") grouped[key].assessmentTypes.add(`${item.assessmentTenure} (${item.assessmentType})`);
    });
    return Object.values(grouped);
  }, [module10CalendarRows]);

  const rowCount = module10CalendarRows.length;
  const subjectCount = [...new Set(module10CalendarRows.map((r) => r.subject))].length;
  const termCount = [...new Set(module10CalendarRows.map((r) => r.term).filter((v) => v !== "—"))].length;

  const rowsWithEvents = module10CalendarRows.filter((row) => row.eventDate);
  const eventsByDate = useMemo(() => {
    const map = {};
    rowsWithEvents.forEach((row) => {
      if (!map[row.eventDate]) map[row.eventDate] = [];
      map[row.eventDate].push({ ...row, _isGenerated: true });
    });
    (module10CustomEvents || []).forEach((ev) => {
      if (!ev.date) return;
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push({ ...ev, _isCustom: true });
    });
    return map;
  }, [rowsWithEvents, module10CustomEvents]);

  const monthWeeks = useMemo(() => buildMonthMatrix(viewYear, viewMonth), [viewYear, viewMonth]);
  const monthLabel = `${MONTH_LABELS[viewMonth - 1]} ${viewYear}`;
  const selectedMonthPrefix = `${viewYear}-${String(viewMonth).padStart(2, "0")}-`;
  const monthEvents = [
    ...rowsWithEvents
      .filter((row) => row.eventDate && row.eventDate.startsWith(selectedMonthPrefix))
      .map((row) => ({ ...row, _isGenerated: true })),
    ...(module10CustomEvents || [])
      .filter((ev) => ev.date && ev.date.startsWith(selectedMonthPrefix))
      .map((ev) => ({ ...ev, _isCustom: true })),
  ].sort((a, b) => {
    const da = a.eventDate || a.date || "";
    const db = b.eventDate || b.date || "";
    return da.localeCompare(db);
  });

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
      return;
    }
    setViewMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
      return;
    }
    setViewMonth((m) => m + 1);
  };

  return (
    <ModuleCard id="module10">
      <ModuleHeader
        step="Module 10"
        title="Academic Calendar"
        description="Creates a structured academic calendar by combining chapter planning and assessment mapping into dated events."
        showStatus={false}
      />

      <InfoBanner>
        The calendar is generated from <strong>Module 6 (chapters)</strong> and <strong>Module 7 (assessment mappings)</strong>, then mapped into dated events for monthly calendar view.
      </InfoBanner>
      {!!(module10LoadError || actionError) && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">
          {module10LoadError || actionError}
        </div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Academic Year" value={activeYear || "-"} meta="Active ACPP plan year" />
        <MetricCard label="Calendar Rows" value={rowCount} meta="Combined chapter and assessment rows" />
        <MetricCard label="Subjects Covered" value={subjectCount} meta="Distinct subjects in academic calendar" />
        <MetricCard label="Custom Events" value={(module10CustomEvents || []).length} meta="Manually added events" />
      </Grid4>

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Calendar Filters">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel>Grade</InputLabel>
              <FormSelect value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}>
                <option value="">All Grades</option>
                {filterGrades.map((g) => <option key={g} value={g}>{g}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Section</InputLabel>
              <FormSelect value={filterSection} onChange={(e) => setFilterSection(e.target.value)}>
                <option value="">All Sections</option>
                {filterSections.map((s) => <option key={s} value={s}>{s}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Subject</InputLabel>
              <FormSelect value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
                <option value="">All Subjects</option>
                {filterSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Term</InputLabel>
              <FormSelect value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)}>
                <option value="">All Terms</option>
                {["Term 1", "Term 2", "Term 3", "Term 4"].map((t) => <option key={t} value={t}>{t}</option>)}
              </FormSelect>
            </div>
          </div>
          <BtnRow>
            <BtnPrimary disabled={!canEditModules1to5 || saving} onClick={generate}>Generate Draft</BtnPrimary>
            <BtnSecondary disabled={!canEditModules1to5 || saving} onClick={save}>Save Year Data</BtnSecondary>
            <BtnLight disabled={saving} onClick={refresh}>Refresh Year Data</BtnLight>
            <BtnLight onClick={() => { setFilterGrade(""); setFilterSection(""); setFilterSubject(""); setFilterTerm(""); }}>Reset Filters</BtnLight>
          </BtnRow>
        </SoftPanel>
      ) : null}


      <SectionDivider />

      <SoftPanel title="Monthly Calendar View">
        <div className="flex items-center justify-between mb-3">
          <BtnLight onClick={goPrevMonth}>Previous</BtnLight>
          <p className="text-[14px] font-bold text-[#1f2937] m-0">{monthLabel}</p>
          <div className="flex items-center gap-2">
            {canEditModules1to5 && (
              <button
                type="button"
                onClick={() => openAddModal(`${viewYear}-${String(viewMonth).padStart(2, "0")}-01`)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#D67C2C] text-white text-[12px] font-semibold hover:bg-[#c06928] transition-colors cursor-pointer"
              >
                + Add Event
              </button>
            )}
            <BtnLight onClick={goNextMonth}>Next</BtnLight>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-[#4b5563] mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthWeeks.flat().map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} className="min-h-[88px] rounded-lg border border-transparent bg-transparent" />;
            const dateKey = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const dayEvents = eventsByDate[dateKey] || [];
            return (
              <div
                key={dateKey}
                onClick={() => canEditModules1to5 && openAddModal(dateKey)}
                className={`min-h-[88px] rounded-lg border border-[#d8e4f5] bg-[#f8fbff] p-1 ${canEditModules1to5 ? "cursor-pointer hover:border-[#D67C2C] hover:bg-orange-50 transition-colors" : ""}`}
                title={canEditModules1to5 ? `Click to add event on ${dateKey}` : undefined}
              >
                <p className="text-[11px] font-bold text-[#1f2937] m-0 mb-1">{day}</p>
                <div className="flex flex-col gap-1">
                  {dayEvents.slice(0, 3).map((ev, i) => {
                    if (ev._isCustom) {
                      const clr = CUSTOM_EVENT_COLORS[ev.eventType] || CUSTOM_EVENT_COLORS.Other;
                      return (
                        <span key={`${dateKey}-c-${i}`} className={`text-[10px] px-1 py-0.5 rounded border truncate font-medium ${clr.bg} ${clr.text} ${clr.border}`}>
                          ● {ev.title}
                        </span>
                      );
                    }
                    return (
                      <span key={`${dateKey}-${i}`} className="text-[10px] px-1 py-0.5 rounded bg-[#e8f1ff] text-[#1558b0] border border-[#cfe0ff] truncate">
                        {ev.subject}
                      </span>
                    );
                  })}
                  {dayEvents.length > 3 && <span className="text-[10px] text-[#6b7280]">+{dayEvents.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
        {/* Colour legend */}
        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-[#e5e7eb]">
          <span className="flex items-center gap-1 text-[11px] text-[#6b7280]">
            <span className="inline-block w-3 h-3 rounded bg-[#e8f1ff] border border-[#cfe0ff]" /> Generated
          </span>
          {CUSTOM_EVENT_TYPES.map((type) => {
            const clr = CUSTOM_EVENT_COLORS[type];
            return (
              <span key={type} className="flex items-center gap-1 text-[11px] text-[#6b7280]">
                <span className={`inline-block w-3 h-3 rounded border ${clr.bg} ${clr.border}`} /> {type}
              </span>
            );
          })}
        </div>
      </SoftPanel>

      <SectionDivider />

      <SoftPanel title="Events in Selected Month">
        <TableWrap>
          <thead>
            <tr>
              <Th>Date</Th><Th>Event</Th><Th>Type</Th><Th>Grade</Th><Th>Section</Th><Th>Subject</Th><Th>Assessment</Th>
              {canEditModules1to5 && <Th>Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {monthEvents.length === 0 ? (
              <tr><td colSpan={canEditModules1to5 ? 8 : 7}><EmptyState>No events in this month.</EmptyState></td></tr>
            ) : monthEvents.map((item, i) => {
              if (item._isCustom) {
                const clr = CUSTOM_EVENT_COLORS[item.eventType] || CUSTOM_EVENT_COLORS.Other;
                return (
                  <tr key={`custom-${item.id}-${i}`} className="bg-orange-50/40">
                    <Td>{item.date}</Td>
                    <Td><span className="font-medium">{item.title}</span>{item.notes && <span className="ml-1 text-[11px] text-gray-400">— {item.notes}</span>}</Td>
                    <Td><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${clr.bg} ${clr.text} ${clr.border}`}>{item.eventType}</span></Td>
                    <Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td>
                    {canEditModules1to5 && (
                      <Td>
                        <ActionsCell>
                          <ActionIconButton icon={Pencil} tone="edit" title="Edit" onClick={() => handleEditCustomEvent(item)} />
                          <ActionIconButton icon={Trash2} tone="delete" title="Delete" onClick={() => handleDeleteCustomEvent(item.id)} />
                        </ActionsCell>
                      </Td>
                    )}
                  </tr>
                );
              }
              return (
                <tr key={`gen-${item.eventDate}-${i}`}>
                  <Td>{item.eventDate}</Td>
                  <Td>{item.eventTitle || deriveEventTitle(item)}</Td>
                  <Td><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">Generated</span></Td>
                  <Td>{item.grade}</Td>
                  <Td>{item.section}</Td>
                  <Td>{item.subject}</Td>
                  <Td>{item.assessmentTenure !== "—" ? <TableChip>{item.assessmentTenure}</TableChip> : "—"}</Td>
                  {canEditModules1to5 && <Td>—</Td>}
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      <SoftPanel title="Detailed Academic Calendar Table">
        <TableWrap>
          <thead>
            <tr>
              <Th>#</Th><Th>Acad. Year</Th><Th>Date</Th><Th>Grade</Th><Th>Section</Th>
              <Th>Subject</Th><Th>Term</Th><Th>Unit No.</Th><Th>Unit Name</Th>
              <Th>Chapter</Th><Th>Assessment Tenure</Th><Th>Assessment Type</Th><Th>Sub-Type(s)</Th>
            </tr>
          </thead>
          <tbody>
            {module10CalendarRows.length === 0 ? (
              <tr><td colSpan={13}><EmptyState>Academic calendar rows will appear here after generation.</EmptyState></td></tr>
            ) : module10CalendarRows.map((item, i) => (
              <tr key={i}>
                <Td>{i + 1}</Td>
                <Td>{item.academicYear}</Td><Td>{item.eventDate || "—"}</Td><Td>{item.grade}</Td><Td>{item.section}</Td>
                <Td>{item.subject}</Td><Td>{item.term}</Td>
                <Td>{item.unitNo}</Td><Td>{item.unitName}</Td><Td>{item.chapter}</Td>
                <Td>{item.assessmentTenure !== "—" ? <TableChip>{item.assessmentTenure}</TableChip> : "—"}</Td>
                <Td>{item.assessmentType !== "—" ? <TableChip>{item.assessmentType}</TableChip> : "—"}</Td>
                <Td>{item.assessmentSubTypes !== "—" ? item.assessmentSubTypes.split(", ").map((s) => <TableChip key={s}>{s}</TableChip>) : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      {/* ── Custom Events Management ───────────────────────────────────────── */}
      <SoftPanel title="Custom Events">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[13px] text-[#6b7280] m-0">
            Add school-wide events — holidays, exams, parent meetings, special activities — that are not auto-generated from curriculum modules.
          </p>
          {canEditModules1to5 && (
            <button
              type="button"
              onClick={() => openAddModal("")}
              className="flex-shrink-0 ml-4 px-3 py-1.5 rounded-lg bg-[#D67C2C] text-white text-[12px] font-semibold hover:bg-[#c06928] transition-colors cursor-pointer"
            >
              + Add Custom Event
            </button>
          )}
        </div>
        <TableWrap>
          <thead>
            <tr>
              <Th>Date</Th><Th>Title</Th><Th>Type</Th><Th>Notes</Th>
              {canEditModules1to5 && <Th>Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {(!module10CustomEvents || !module10CustomEvents.length) ? (
              <tr><td colSpan={canEditModules1to5 ? 5 : 4}><EmptyState>No custom events added yet. Click "+ Add Custom Event" to get started.</EmptyState></td></tr>
            ) : [...module10CustomEvents].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((ev) => {
              const clr = CUSTOM_EVENT_COLORS[ev.eventType] || CUSTOM_EVENT_COLORS.Other;
              return (
                <tr key={ev.id}>
                  <Td>{ev.date}</Td>
                  <Td className="font-medium">{ev.title}</Td>
                  <Td><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${clr.bg} ${clr.text} ${clr.border}`}>{ev.eventType}</span></Td>
                  <Td>{ev.notes || "—"}</Td>
                  {canEditModules1to5 && (
                    <Td>
                      <ActionsCell>
                        <ActionIconButton icon={Pencil} tone="edit" title="Edit" onClick={() => handleEditCustomEvent(ev)} />
                        <ActionIconButton icon={Trash2} tone="delete" title="Delete" onClick={() => handleDeleteCustomEvent(ev.id)} />
                      </ActionsCell>
                    </Td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      <SoftPanel title="Leader Calendar Summary">
        <TableWrap>
          <thead>
            <tr>
              <Th>Grade</Th><Th>Section</Th><Th>Subject</Th>
              <Th>Terms</Th><Th>Units</Th><Th>Chapters</Th>
              <Th>Assessments</Th><Th>Assessment Types Used</Th>
            </tr>
          </thead>
          <tbody>
            {summaryGroups.length === 0 ? (
              <tr><td colSpan={8}><EmptyState>Leader summary will appear here after calendar generation.</EmptyState></td></tr>
            ) : summaryGroups.map((item, i) => (
              <tr key={i}>
                <Td>{item.grade}</Td><Td>{item.section}</Td><Td>{item.subject}</Td>
                <Td>{item.terms.size}</Td><Td>{item.units.size}</Td><Td>{item.chapters.size}</Td>
                <Td>{item.assessmentTypes.size}</Td>
                <Td>{[...item.assessmentTypes].map((t) => <TableChip key={t}>{t}</TableChip>) || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>
      {/* ── Custom Event Modal ───────────────────────────────────────────────── */}
      {customModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-[#1D3469] to-[#2a4a8a] text-white px-6 py-4">
              <h3 className="text-[16px] font-bold m-0">
                {customModal.draft.id ? "Edit Custom Event" : "Add Custom Event"}
              </h3>
              <p className="text-[12px] text-blue-200 m-0 mt-1">
                This event will appear on the academic calendar and can be saved with the calendar data.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#1D3469] mb-1">Event Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={customModal.draft.title}
                  onChange={(e) => setModalField("title", e.target.value)}
                  placeholder="e.g. Republic Day Holiday"
                  className="w-full rounded-xl border border-[#d8e4f5] px-3 py-2 text-[13px] focus:outline-none focus:border-[#D67C2C] focus:ring-1 focus:ring-[#D67C2C]"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#1D3469] mb-1">Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={customModal.draft.date}
                  onChange={(e) => setModalField("date", e.target.value)}
                  className="w-full rounded-xl border border-[#d8e4f5] px-3 py-2 text-[13px] focus:outline-none focus:border-[#D67C2C] focus:ring-1 focus:ring-[#D67C2C]"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#1D3469] mb-1">Event Type</label>
                <select
                  value={customModal.draft.eventType}
                  onChange={(e) => setModalField("eventType", e.target.value)}
                  className="w-full rounded-xl border border-[#d8e4f5] px-3 py-2 text-[13px] focus:outline-none focus:border-[#D67C2C] focus:ring-1 focus:ring-[#D67C2C]"
                >
                  {CUSTOM_EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#1D3469] mb-1">Notes <span className="text-[#9ca3af] font-normal">(optional)</span></label>
                <textarea
                  value={customModal.draft.notes}
                  onChange={(e) => setModalField("notes", e.target.value)}
                  placeholder="Additional details about this event..."
                  rows={2}
                  className="w-full rounded-xl border border-[#d8e4f5] px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-[#D67C2C] focus:ring-1 focus:ring-[#D67C2C]"
                />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl border border-[#d8e4f5] text-[13px] font-semibold text-[#6b7280] hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCustomEvent}
                disabled={customModal.saving}
                className="flex-1 py-2.5 rounded-xl bg-[#D67C2C] text-white text-[13px] font-semibold hover:bg-[#c06928] transition-colors cursor-pointer disabled:opacity-60"
              >
                {customModal.saving ? "Saving…" : (customModal.draft.id ? "Update Event" : "Add Event")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModuleCard>
  );
}
